import { Ref, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContractDetailsInput, ContractDetailsSchema } from "../validation/contract-settings";
import { ContractTradeType } from "@/types";
import { useGetCreateOrderValues } from "@/services/Orders";
import { getCompanyCreditById, useGetCompanySpecificProductById } from "@/services/Company";
import { CreateContractWizard } from "../helpers/helpers";
import { useQueryClient } from "@tanstack/react-query";
import { Grid } from "@mantine/core";
import { useAuth } from "@/contexts/AuthContext";

import Text from "@/components/atoms/Text";
import ContractEndTypeEnum from "@/constants/enums/ContractEndTypeEnum";
import Divider from "@/components/atoms/Divider";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import Group from "@/components/atoms/Group";
import Stack from "@/components/atoms/Stack";
import Checkbox from "@/components/atoms/Checkbox";
import Alert from "@/components/atoms/Alert";
import dayjs from "dayjs";
import TextInput from "@/components/atoms/TextInput";
import SelectProductMeasureUnit from "@/components/molecules/SelectInputs/SelectProductMeasureUnit";
import SelectTeamMember from "@/components/molecules/SelectInputs/SelectTeamMember";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import DatePickerInput from "@/components/atoms/DatePickerInput";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  wizardData: CreateContractWizard;
  ref?: Ref<HTMLFormElement>;
}

function ContractSettings({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { currentCompany } = useAuth();

  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [isLoadingCreditCheck, setIsLoadingCreditCheck] = useState(false);

  const { data: createOrderValues } = useGetCreateOrderValues();
  const { data: companySpecificProduct } = useGetCompanySpecificProductById(wizardData.companySpecificProductId);

  const contractTradeTypeOptions = [
    { value: ContractTradeTypeEnum.SELL, label: t("contracts:sale") },
    { value: ContractTradeTypeEnum.BUY, label: t("contracts:purchase") },
  ];
  const contractTradeByOptions = [
    { value: ContractEndTypeEnum.AMOUNT, label: t("common:amount") },
    { value: ContractEndTypeEnum.DATEPERIOD, label: t("common:date") },
  ];
  const radioButtonGroupOptions = [
    { value: "YES", label: t("common:yes") },
    { value: "NO", label: t("common:no") },
  ];

  const { handleSubmit, control, watch, setError, setValue, getValues } = useForm<ContractDetailsInput>({
    resolver: zodResolver(ContractDetailsSchema),
    reValidateMode: "onChange",
    defaultValues: {
      contractEndType: wizardData.contractEndType,
      contractStartDate: wizardData.contractStartDate,
      contractEndDate: wizardData.contractEndDate ? wizardData.contractEndDate : undefined,
      amount: wizardData.amount ?? "0",
      amountUnitId: wizardData.amountUnitId,
      name: wizardData.name,
      contractType: wizardData.contractType,
      automaticSalesApproval: wizardData.automaticSalesApproval,
      contractManagerId: wizardData.contractManagerId,
      contactEmail: wizardData.contactEmail,
      contactPhone: wizardData.contactPhone,
      counterpartyContactEmail: wizardData.counterpartyContactEmail,
      counterpartyContactPhone: wizardData.counterpartyContactPhone,
      tradeAtOwnRisk: wizardData.tradeAtOwnRisk,
      template: wizardData.template,
      templateName: wizardData.templateName,
    },
  });

  const onSubmit = async (data: ContractDetailsInput) => {
    if (isLoadingCreditCheck) return;
    const validMeasureUnit = validateNonWeightMeasureUnit(data);
    if (!validMeasureUnit.isValid) {
      setError(
        validMeasureUnit.field as keyof ContractDetailsInput,
        {
          type: "manual",
          message: validMeasureUnit.message,
        },
        { shouldFocus: true }
      );
    } else if (showCreditWarning && !data.tradeAtOwnRisk) {
      setError(
        "tradeAtOwnRisk",
        {
          type: "manual",
          message: "validation:creditWarningError",
        },
        { shouldFocus: true }
      );
    } else {
      onChangeWizardData(data);
      onValidationSuccess(data);
    }
  };

  const validateNonWeightMeasureUnit = (formData: ContractDetailsInput) => {
    if (formData.contractEndType === ContractEndTypeEnum.AMOUNT) {
      const selectedMeasureUnit = createOrderValues?.units.find((unit) => unit.id === formData.amountUnitId);

      if (selectedMeasureUnit?.value === "stk" && companySpecificProduct?.kgPerStk === null) {
        return {
          isValid: false,
          field: "amountUnitId",
          message: t("validation:kgPerStkUnitRequired"),
        };
      } else if (selectedMeasureUnit?.value === "meter" && companySpecificProduct?.kgPerMeter === null) {
        return {
          isValid: false,
          field: "amountUnitId",
          message: t("validation:kgPerMeterUnitRequired"),
        };
      } else if (selectedMeasureUnit?.value === "m3" && companySpecificProduct?.kgPerM3 === null) {
        return {
          isValid: false,
          field: "amountUnitId",
          message: t("validation:kgPerM3UnitRequired"),
        };
      } else {
        return { isValid: true };
      }
    }
    return { isValid: true };
  };

  useEffect(() => {
    const contractTradeType = getValues("contractType");
    onChangeContractType(contractTradeType).then();
  }, []);

  const onChangeContractType = async (contractTradeType: ContractTradeType | string) => {
    const buyerCompany =
      contractTradeType === ContractTradeTypeEnum.SELL ? wizardData.tradingPartner : currentCompany?.companyId;

    if (!buyerCompany) return;
    try {
      setIsLoadingCreditCheck(true);
      const freshBuyerCompanyCreditQuery = await queryClient.fetchQuery({
        queryKey: ["getCompanyCreditById", buyerCompany],
        queryFn: () => getCompanyCreditById(buyerCompany),
      });

      const hasCreditLimit = freshBuyerCompanyCreditQuery.data && freshBuyerCompanyCreditQuery.data.creditLimit > 0;
      if (!hasCreditLimit) {
        setShowCreditWarning(true);
      } else {
        setShowCreditWarning(false);
        setValue("tradeAtOwnRisk", false);
      }

      setValue("contractType", contractTradeType);
    } catch {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("validation:fetchingCreditInfoError"))
      );
    } finally {
      setIsLoadingCreditCheck(false);
    }
  };

  return (
    <>
      <form ref={ref} onSubmit={handleSubmit(onSubmit)} data-testid="contract-settings-form">
        <Grid>
          <Grid.Col span={{ base: 12, lg: 3 }}>
            <Text color="secondary" size="sm" style={{ display: "inline" }}>
              {t("contracts:contractName")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => {
                return (
                  <TextInput
                    data-testid="name"
                    placeholder={t("contracts:contractName")}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    {...field}
                  />
                );
              }}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 3 }}>
            <Text color="secondary" size="sm" style={{ display: "inline" }}>
              {t("contracts:startDate")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <Controller
              control={control}
              name="contractStartDate"
              render={({ field: { onChange, value }, fieldState }) => {
                return (
                  <DatePickerInput
                    value={value}
                    onChange={onChange}
                    error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="contractType"
              render={({ field: { value } }) => (
                <RadioButtonGroup
                  title={t("contracts:contractType")}
                  name={"contractType"}
                  options={contractTradeTypeOptions}
                  defaultValue={value}
                  value={value}
                  testId={"contractType"}
                  titleTooltipText={t("contracts:contractTypeExplanation")}
                  onValueChange={async (value: ContractTradeType) => {
                    await onChangeContractType(value);
                  }}
                  inlineTitle={true}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Controller
              control={control}
              name="contractEndType"
              render={({ field: { onChange, value } }) => (
                <RadioButtonGroup
                  title={t("contracts:tradeBy")}
                  name={"contractEndType"}
                  options={contractTradeByOptions}
                  testId={"contractEndType"}
                  defaultValue={value}
                  onValueChange={(value: string) => {
                    onChange(value);
                  }}
                  inlineTitle={true}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Controller
              control={control}
              name="poNumber"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  data-testid={"poNumber"}
                  id="create-contract-purchase-order-number"
                  label={t("contracts:purchaseOrderNumber")}
                  value={value ?? ""}
                  onChange={onChange}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            {watch("contractEndType") === ContractEndTypeEnum.AMOUNT && (
              <>
                <Group justify="flex-start" align={"flex-start"} wrap={"nowrap"} gap={0}>
                  <Controller
                    control={control}
                    name="amount"
                    render={({ field, fieldState }) => {
                      return (
                        <TextInput
                          data-testid={"amount"}
                          id="create-contract-amount"
                          label={t("common:amount")}
                          type="number"
                          placeholder={t("common:amount")}
                          error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                          {...field}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name="amountUnitId"
                    render={({ field: { onChange, value }, fieldState }) => (
                      <SelectProductMeasureUnit
                        id="create-contract-unit"
                        data-testid={"amountUnitId"}
                        label={t("common:unit")}
                        onChange={(value) => onChange(value ? Number(value) : null)}
                        value={value.toString()}
                        maw={100}
                        placeholder={t("enumerables:weightUnits")}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                        valueKey={"id"}
                        labelKey={"value"}
                      />
                    )}
                  />
                </Group>
                <Divider></Divider>
                <Text size={"xs"} color={"secondary"}>
                  {t("contracts:toSelectNonWeightMeasureUnit")}
                </Text>
              </>
            )}
            {watch("contractEndType") === ContractEndTypeEnum.DATEPERIOD && (
              <Controller
                control={control}
                name="contractEndDate"
                render={({ field: { onChange, value }, fieldState }) => {
                  const dateValue = value && dayjs(value).isValid() ? dayjs(value).toDate() : null;
                  return (
                    <DatePickerInput
                      label={t("contracts:endDate")}
                      value={dateValue}
                      onChange={onChange}
                      error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                    />
                  );
                }}
              />
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Stack gap={"25px"}>
              <Text color="secondary" size="sm" ta={{ base: "left", sm: "right" }}>
                {t("contracts:automaticSalesMatch")}
              </Text>
              <Controller
                control={control}
                name="automaticSalesApproval"
                render={({ field: { onChange, value } }) => (
                  <RadioButtonGroup
                    name={"automaticSalesApproval"}
                    options={radioButtonGroupOptions}
                    testId={"automaticSalesApproval"}
                    defaultValue={value}
                    onValueChange={(value: string) => {
                      onChange(value);
                    }}
                    justify="right"
                  />
                )}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 3 }}>
            <Text color="secondary" size="sm" style={{ display: "inline" }}>
              {t("contracts:contractResponsible")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 9 }}>
            <Controller
              control={control}
              name="contractManagerId"
              render={({ field: { onChange, value }, fieldState }) => (
                <SelectTeamMember
                  id={"contractManagerId"}
                  data-testid={"contractManagerId"}
                  error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                  value={value.toString()}
                  onChange={(value) => onChange(value ? Number(value) : null)}
                  valueKey={"id"}
                  labelKey={["firstName", "lastName"]}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 3 }}>
            <Text color="secondary" size="sm" style={{ display: "inline" }}>
              {t("contracts:contact")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 6, lg: 5 }}>
            <Controller
              control={control}
              name="contactEmail"
              render={({ field, fieldState }) => (
                <TextInput
                  data-testid={"contactEmail"}
                  id="create-contract-contact-email"
                  placeholder={t("common:email")}
                  error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                  {...field}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, lg: 4 }}>
            <Controller
              control={control}
              name="contactPhone"
              render={({ field, fieldState }) => (
                <TextInput
                  data-testid={"contactPhone"}
                  id="create-contract-contact-phone"
                  placeholder={t("common:phone")}
                  error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                  {...field}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 3 }}>
            <Text color="secondary" size="sm" style={{ display: "inline" }}>
              {t("contracts:contactCounterparty")}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 6, lg: 5 }}>
            <Controller
              control={control}
              name="counterpartyContactEmail"
              render={({ field, fieldState }) => (
                <TextInput
                  data-testid={"counterpartyContactEmail"}
                  id="create-contract-counterparty-email"
                  placeholder={t("common:email")}
                  error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                  {...field}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, lg: 4 }}>
            <Controller
              control={control}
              name="counterpartyContactPhone"
              render={({ field, fieldState }) => (
                <TextInput
                  data-testid={"counterpartyContactPhone"}
                  id="create-contract-counterparty-phone"
                  placeholder={t("common:phone")}
                  error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                  {...field}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
          {showCreditWarning && (
            <>
              <Grid.Col span={{ base: 6 }}>
                <Alert color="warning" title={t("contracts:creditWarningTitle")}>
                  {t("contracts:creditWarningCreateContract")}
                </Alert>
              </Grid.Col>
              <Grid.Col span={{ base: 6 }}>
                <Controller
                  control={control}
                  name="tradeAtOwnRisk"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Checkbox
                      label={t("contracts:acceptRisk")}
                      data-testid={"tradeAtOwnRisk"}
                      checked={value}
                      onChange={(event) => onChange(event.currentTarget.checked)}
                      error={error?.message ? t(error.message) : undefined}
                    />
                  )}
                />
              </Grid.Col>
            </>
          )}
          <Grid.Col span={12}>
            <Group justify={"space-between"}>
              <Text color="secondary" size="sm" ta={{ base: "left", sm: "right" }}>
                {t("contracts:saveAsTemplate")}
              </Text>
              <Controller
                control={control}
                name="template"
                render={({ field: { onChange } }) => (
                  <RadioButtonGroup
                    name={"template"}
                    options={radioButtonGroupOptions}
                    defaultValue={"NO"}
                    testId={"template"}
                    onValueChange={(value: string) => {
                      onChange(value);
                    }}
                    justify="right"
                  />
                )}
              />
            </Group>
          </Grid.Col>
          {watch("template") === "YES" && (
            <Grid.Col span={12}>
              <Controller
                control={control}
                name="templateName"
                render={({ field, fieldState }) => (
                  <TextInput
                    data-testid="templateName"
                    placeholder={t("contracts:templateName")}
                    error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
}

export default ContractSettings;
